import { z } from "zod";
import { isSupportedTimeZone } from "./calendar-time.js";
/** Scheduling is independent of the provider used by the selected server connection. */
export const SCHEDULING_LIMITS = {
    ranges: 50,
    calendars: 20,
    horizonDays: 90,
    availabilityDays: 31,
    startStepMinutes: 15,
    availabilityMaxAgeMs: 30_000,
};
const identifier = z.string().trim().min(1).max(256);
export const schedulingInstantSchema = z.iso.datetime({ offset: true }).refine((value) => Number.isFinite(Date.parse(value)), "Expected a valid instant");
export const schedulingTimeZoneSchema = z.string().min(1).max(100).refine(isSupportedTimeZone, "Unknown time zone");
export const schedulingIntervalSchema = z.object({
    start: schedulingInstantSchema,
    end: schedulingInstantSchema,
}).strict().refine((value) => Date.parse(value.end) > Date.parse(value.start), {
    message: "The end must be after the start", path: ["end"],
});
export const schedulingRangeSchema = z.object({
    id: identifier,
    start: schedulingInstantSchema,
    end: schedulingInstantSchema,
}).strict().refine((value) => Date.parse(value.end) > Date.parse(value.start), {
    message: "The end must be after the start", path: ["end"],
});
/** No credentials, private events or provider account identifiers cross this boundary. */
export const schedulingPublishInputSchema = z.object({
    connectionId: identifier,
    destinationCalendarId: identifier,
    conflictCalendarIds: z.array(identifier).min(1).max(SCHEDULING_LIMITS.calendars),
    title: z.string().trim().min(1).max(200),
    hostDisplayName: z.string().trim().min(1).max(200),
    location: z.string().trim().max(500).optional(),
    durationMinutes: z.number().int().min(5).max(480),
    timeZone: schedulingTimeZoneSchema,
    minimumNoticeMinutes: z.number().int().min(0).max(43_200),
    expiresAt: schedulingInstantSchema,
    ranges: z.array(schedulingRangeSchema).min(1).max(SCHEDULING_LIMITS.ranges),
}).strict().superRefine((value, ctx) => {
    const unique = (items) => new Set(items).size === items.length;
    if (!unique(value.ranges.map(({ id }) => id))) {
        ctx.addIssue({ code: "custom", path: ["ranges"], message: "Range IDs must be unique" });
    }
    if (!unique(value.conflictCalendarIds)) {
        ctx.addIssue({ code: "custom", path: ["conflictCalendarIds"], message: "Conflict calendars must be unique" });
    }
    if (!value.conflictCalendarIds.includes(value.destinationCalendarId)) {
        ctx.addIssue({ code: "custom", path: ["conflictCalendarIds"], message: "Include the destination calendar" });
    }
    const first = Math.min(...value.ranges.map(({ start }) => Date.parse(start)));
    const last = Math.max(...value.ranges.map(({ end }) => Date.parse(end)));
    if (last - first > SCHEDULING_LIMITS.horizonDays * 86_400_000) {
        ctx.addIssue({ code: "custom", path: ["ranges"], message: "Availability spans more than 90 days" });
    }
    if (Date.parse(value.expiresAt) > last) {
        ctx.addIssue({ code: "custom", path: ["expiresAt"], message: "Expiry must not follow the last range" });
    }
});
export const schedulingAvailabilityWindowSchema = schedulingIntervalSchema.refine(({ start, end }) => Date.parse(end) - Date.parse(start) <= SCHEDULING_LIMITS.availabilityDays * 86_400_000, "An availability request may span at most 31 days");
export const schedulingBookingInputSchema = z.object({
    proposalRevision: z.number().int().positive(),
    start: schedulingInstantSchema,
    name: z.string().trim().min(1).max(200),
    email: z.email().max(254),
}).strict();
export const schedulingProposalStateSchema = z.enum(["published", "claimed", "booked", "revoked", "expired"]);
export const schedulingBookingStateSchema = z.enum(["pending", "reconciling", "confirmed", "cancelling", "cancelled", "failed"]);
export const schedulingFailureCodeSchema = z.enum([
    "unavailable", "expired", "revoked", "already_booked", "availability_unknown",
    "connection_required", "idempotency_conflict", "stale_proposal", "provider_rejected",
]);
/** Deliberately distinct from the owner record: a public link reveals no calendar or guest identities. */
export const schedulingPublicProposalSchema = z.object({
    title: z.string().max(200),
    hostDisplayName: z.string().max(200),
    location: z.string().max(500).optional(),
    durationMinutes: z.number().int().min(5).max(480),
    timeZone: schedulingTimeZoneSchema,
    revision: z.number().int().positive(),
    state: schedulingProposalStateSchema,
}).strict();
export const schedulingPublicAvailabilitySchema = z.object({
    revision: z.number().int().positive(),
    checkedAt: schedulingInstantSchema,
    slots: z.array(schedulingIntervalSchema).max(3_000),
}).strict();
/** A missing provider result is never represented as an empty busy list. */
export const schedulingCalendarAvailabilitySchema = z.discriminatedUnion("status", [
    z.object({ calendarId: identifier, status: z.literal("complete"), busy: z.array(schedulingIntervalSchema) }).strict(),
    z.object({ calendarId: identifier, status: z.literal("unavailable") }).strict(),
]);
export const schedulingAvailabilitySnapshotSchema = z.object({
    window: schedulingAvailabilityWindowSchema,
    observedAt: schedulingInstantSchema,
    calendars: z.array(schedulingCalendarAvailabilitySchema).min(1).max(SCHEDULING_LIMITS.calendars),
}).strict();
